import React, { useCallback, useMemo, useState, useEffect } from 'react';

import { tokens } from '@sparkpost/design-tokens';
import { Box, Button, Layout, Panel, Progress, Stack, Text } from 'src/components/matchbox';
import { Form } from 'src/components/tracking';
import AlertSummary from '../AlertSummary/AccountUsageAlertSummary';
import { TAB_DETAILS_AND_NOTIFICATIONS, TAB_METRIC_AND_FILTERING } from '../constants';
import Footer from '../Footer';
import Details from '../Forms/Details';
import UsageMetric from '../Forms/UsageMetric';
import Notifications from '../Forms/Notifications';
import { useAlertForm } from '../useAlertForm';
import { SENDING_LIMIT_ALERT_METRIC } from '../../constants/alertTypes';
import { useModal } from 'src/hooks';
import ExitSetupModal from './ExitSetupModal';

const tabsLabels = ['Metric and Filtering', 'Details and Notifications'];

export default function UsageSetup({ goToTypeSelection, isEditingAlert, alertMutation }) {
  const [tab, setTab] = useState(TAB_METRIC_AND_FILTERING);
  const { dispatch, validatePageFields } = useAlertForm();
  const { closeModal, openModal: openExitModal, isModalOpen } = useModal();

  useEffect(() => {
    dispatch({ type: 'UPDATE_METRIC_DATA', data: { selectedMetric: SENDING_LIMIT_ALERT_METRIC } });
  }, [dispatch]);

  const handleExit = useCallback(() => {
    closeModal();
    dispatch({ type: 'CLEAN_UP' });
    goToTypeSelection();
  }, [closeModal, dispatch, goToTypeSelection]);

  const handleCreateAlert = useCallback(() => {
    const hasErrors = validatePageFields(tab);

    if (hasErrors) return;

    alertMutation.mutate({});
  }, [alertMutation, tab, validatePageFields]);

  const handleSkip = useCallback(() => {
    setTab((previousTab) => previousTab + 1);
  }, []);

  const FormBody = useMemo(() => {
    switch (tab) {
      case TAB_METRIC_AND_FILTERING:
        return UsageMetric;
      case TAB_DETAILS_AND_NOTIFICATIONS:
        return Details;
      default:
        return UsageMetric;
    }
  }, [tab]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
  }, []);

  const goToMetricTab = useCallback(() => {
    setTab(TAB_METRIC_AND_FILTERING);
  }, []);

  const goToDetailsAndNotificationsTab = useCallback(() => {
    const hasErrors = validatePageFields(tab);

    if (hasErrors) return;

    setTab(TAB_DETAILS_AND_NOTIFICATIONS);
  }, [tab, validatePageFields]);

  const handleContinue = useCallback(() => {
    switch (tab) {
      case TAB_METRIC_AND_FILTERING:
        goToDetailsAndNotificationsTab();
        break;
      default:
        break;
    }
  }, [tab, goToDetailsAndNotificationsTab]);

  return (
    <Form onSubmit={handleSubmit} id="new-alert-create-form">
      <ExitSetupModal isModalOpen={isModalOpen} closeModal={closeModal} handleExit={handleExit} />
      <Layout>
        <Layout.Section annotated>
          <Progress>
            <Progress.Step
              completed={tab > TAB_METRIC_AND_FILTERING}
              selected={tab === TAB_METRIC_AND_FILTERING}
              onClick={goToMetricTab}
            >
              {tabsLabels[0]}
            </Progress.Step>
            <Progress.Step
              completed={tab > TAB_DETAILS_AND_NOTIFICATIONS}
              selected={tab === TAB_DETAILS_AND_NOTIFICATIONS}
              onClick={goToDetailsAndNotificationsTab}
            >
              {tabsLabels[1]}
            </Progress.Step>
          </Progress>
        </Layout.Section>
        <Layout.Section>
          <Box mb={tokens.spacing_900} maxWidth="786px">
            <Stack space={tokens.spacing_500}>
              <Panel>
                <Panel.Section>
                  <FormBody />
                </Panel.Section>
              </Panel>

              {tab === TAB_DETAILS_AND_NOTIFICATIONS && (
                <Panel>
                  <Panel.Section>
                    <Notifications />
                  </Panel.Section>
                </Panel>
              )}
              <AlertSummary />
            </Stack>
          </Box>
        </Layout.Section>
      </Layout>
      <Footer label={tabsLabels[tab - 1]}>
        {isEditingAlert && tab !== TAB_DETAILS_AND_NOTIFICATIONS && (
          <Button
            variant="tertiary"
            mr="8px"
            onClick={handleSkip}
            loading={alertMutation.status === 'loading'}
          >
            <Text>Skip</Text>
          </Button>
        )}
        {(tab !== TAB_DETAILS_AND_NOTIFICATIONS || !isEditingAlert) && (
          <Button
            variant="secondary"
            mr="8px"
            onClick={isEditingAlert ? handleCreateAlert : openExitModal}
            loading={alertMutation.status === 'loading'}
          >
            <Text>{isEditingAlert ? 'Save and Exit' : 'Exit'}</Text>
          </Button>
        )}
        {tab === TAB_DETAILS_AND_NOTIFICATIONS ? (
          <Button
            variant="primary"
            onClick={handleCreateAlert}
            loading={alertMutation.status === 'loading'}
          >
            <Text>{isEditingAlert ? 'Save and Exit' : 'Create Alert'}</Text>
          </Button>
        ) : (
          <Button
            variant="primary"
            type="submit"
            onClick={handleContinue}
            loading={alertMutation.status === 'loading'}
          >
            <Text>{isEditingAlert ? 'Save and Continue' : 'Continue'}</Text>
          </Button>
        )}
      </Footer>
    </Form>
  );
}
